import { useEffect, useMemo, useState } from "react";
import { Download, Inbox, Mail, MailOpen, RefreshCw, Trash2 } from "lucide-react";
import { API, api, apiErrToStr, fmtDate, fmtDuration } from "@/lib/api";

export default function Voicemail() {
  const [messages, setMessages] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [ramal, setRamal] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [messagesResponse, extensionsResponse] = await Promise.all([
        api.get("/voicemail/messages", { params: ramal ? { ramal } : {} }),
        api.get("/ramais"),
      ]);
      setMessages(messagesResponse.data || []);
      setExtensions((extensionsResponse.data || []).filter((item) => item.voicemail_enabled));
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || "Nao foi possivel carregar o correio de voz.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), 10000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ramal]);

  const unread = useMemo(() => messages.filter((item) => !item.read).length, [messages]);
  const updateStatus = async (message) => {
    try {
      await api.put(`/voicemail/messages/${message.uuid}/status`, { read: !message.read });
      await load(true);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || "Falha ao atualizar a mensagem.");
    }
  };
  const remove = async (message) => {
    if (!window.confirm(`Excluir a mensagem recebida no ramal ${message.ramal}?`)) return;
    try {
      await api.delete(`/voicemail/messages/${message.uuid}`);
      await load(true);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || "Falha ao excluir a mensagem.");
    }
  };

  return <div className="space-y-5" data-testid="voicemail-page">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Mensagens por ramal</div><h1 className="text-2xl font-semibold tracking-tight">Correio de voz</h1></div>
      <div className="flex items-center gap-2">
        <select className="sbc-input min-w-48" value={ramal} onChange={(event) => setRamal(event.target.value)} aria-label="Filtrar por ramal">
          <option value="">Todos os ramais</option>
          {extensions.map((item) => <option key={item.id} value={item.numero}>{item.numero} - {item.nome}</option>)}
        </select>
        <button className="sbc-btn" onClick={() => load()} title="Atualizar"><RefreshCw size={14}/></button>
      </div>
    </div>
    {error && <div className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">! {error}</div>}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Caixas habilitadas</div><div className="text-2xl font-mono font-semibold mt-1">{extensions.length}</div></div>
      <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Mensagens</div><div className="text-2xl font-mono font-semibold mt-1">{messages.length}</div></div>
      <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Nao ouvidas</div><div className="text-2xl font-mono font-semibold mt-1 text-[color:var(--accent-amber)]">{unread}</div></div>
    </div>
    <div className="sbc-card overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <table className="sbc-table"><thead><tr><th>Status</th><th>Ramal</th><th>Origem</th><th>Recebida em</th><th>Duracao</th><th>Audio</th><th className="text-right">Acoes</th></tr></thead>
          <tbody>{loading ? <tr><td colSpan="7" className="text-center text-[color:var(--text-muted)] py-8">Carregando...</td></tr> : messages.length ? messages.map((message) => <tr key={message.uuid}>
            <td>{message.read ? <span className="sbc-tag"><MailOpen size={11}/> Ouvida</span> : <span className="sbc-tag tag-online"><Mail size={11}/> Nova</span>}</td>
            <td><div className="font-mono">{message.ramal}</div><div className="text-[10px] text-[color:var(--text-muted)]">{message.ramal_nome}</div></td>
            <td><div>{message.caller_name}</div><div className="font-mono text-[10px] text-[color:var(--text-muted)]">{message.caller_number}</div></td>
            <td className="font-mono text-xs whitespace-nowrap">{fmtDate(message.created_at)}</td>
            <td className="font-mono text-xs">{fmtDuration(message.duration_seconds)}</td>
            <td>{message.audio_available ? <audio className="h-8 w-56" controls preload="none" src={`${API}/voicemail/messages/${message.uuid}/audio`}/> : <span className="text-xs text-[color:var(--text-muted)]">Indisponivel</span>}</td>
            <td className="text-right"><div className="inline-flex gap-1">
              <button className="sbc-btn" onClick={() => updateStatus(message)} title={message.read ? "Marcar como nao ouvida" : "Marcar como ouvida"}>{message.read ? <Mail size={12}/> : <MailOpen size={12}/>}</button>
              {message.audio_available && <a className="sbc-btn" href={`${API}/voicemail/messages/${message.uuid}/audio?download=true`} title="Baixar audio"><Download size={12}/></a>}
              <button className="sbc-btn sbc-btn-danger" onClick={() => remove(message)} title="Excluir"><Trash2 size={12}/></button>
            </div></td>
          </tr>) : <tr><td colSpan="7" className="text-center text-[color:var(--text-muted)] py-10"><Inbox size={24} className="mx-auto mb-2"/>Nenhuma mensagem nesta caixa postal.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  </div>;
}
